// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import { handleApiCall } from "../utils/apiHelper";

export const roleService = {
  /**
   * Get all roles
   */
  getRoles: async () => {
    return handleApiCall(
      () => apiClient.get(`/admin/roles/`),
      "Error fetching roles",
    );
  },

  /**
   * Get role by ID
   */
  getRole: async (roleId) => {
    return handleApiCall(
      () => apiClient.get(`/admin/roles/${roleId}`),
      "Error fetching role",
    );
  },

  /**
   * Create new role
   */
  createRole: async (roleData) => {
    return handleApiCall(
      () => apiClient.post(`/admin/roles/`, roleData),
      "Error creating role",
    );
  },

  /**
   * Update role. Falls back to PATCH if the backend returns 404 on PUT,
   * which can happen on older deployments that only expose PATCH.
   */
  updateRole: async (roleId, roleData) =>
    handleApiCall(async () => {
      try {
        const response = await apiClient.put(
          `/admin/roles/${roleId}`,
          roleData,
        );
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          const patchResponse = await apiClient.patch(
            `/admin/roles/${roleId}`,
            roleData,
          );
          return patchResponse.data;
        }
        throw error;
      }
    }, "Error updating role"),

  /**
   * Delete role
   */
  deleteRole: async (roleId) => {
    return handleApiCall(
      () => apiClient.delete(`/admin/roles/${roleId}`),
      "Error deleting role",
    );
  },

  /**
   * Get role-specific permissions by role ID
   */
  getRolePermissions: async (roleId) => {
    return handleApiCall(
      () => apiClient.get(`/admin/permissions/roles/${roleId}/permissions`),
      "Error fetching role permissions",
    );
  },

  /**
   * Update role permissions
   */
  updateRolePermissions: async (roleId, permissions) => {
    return handleApiCall(
      () =>
        apiClient.put(`/admin/permissions/roles/${roleId}/permissions`, {
          permissions: permissions,
        }),
      "Error updating role permissions",
    );
  },

  /**
   * Get all permissions
   */
  rolePermissionsList: async () => {
    return handleApiCall(
      () => apiClient.get(`/admin/permissions/`),
      "Error fetching role permissions",
    );
  },

  /**
   * Create new permission
   */
  createPermission: async (permissionData) => {
    return handleApiCall(
      () => apiClient.post(`/admin/permissions/`, permissionData),
      "Error creating permission",
    );
  },

  /**
   * Update permission
   */
  updatePermission: async (permissionId, permissionData) => {
    return handleApiCall(
      () => apiClient.put(`/admin/permissions/${permissionId}`, permissionData),
      "Error updating permission",
    );
  },

  /**
   * Delete permission
   */
  deletePermission: async (permissionId) => {
    return handleApiCall(
      () => apiClient.delete(`/admin/permissions/${permissionId}`),
      "Error deleting permission",
    );
  },
};
